import { Controller } from "@hotwired/stimulus"

// Sticky menu of a long page: scrolls smoothly to a section and marks the
// section currently in view. Sections set their own scroll-margin-top so the
// menu does not cover their title. In a scrolling box (a modal body), the
// root value is a selector of that box, the closest ancestor matching it.
export default class extends Controller {
  static targets = ["link"]
  static classes = ["active"]
  static values = { root: String }

  connect () {
    if (!("IntersectionObserver" in window)) return

    // A section counts as current while it crosses the upper part of the viewport, or of the root box.
    const root = this.hasRootValue ? this.element.closest(this.rootValue) : null
    this.observer = new IntersectionObserver(this.update.bind(this), { root, rootMargin: "-20% 0px -70% 0px" })
    this.sections.forEach((section) => this.observer.observe(section))
  }

  disconnect () {
    this.observer?.disconnect()
  }

  go (event) {
    const link = event.currentTarget
    const section = this.sectionFor(link)
    if (!section) return

    event.preventDefault()
    section.scrollIntoView({ behavior: "smooth", block: "start" })
    history.replaceState(history.state, "", link.hash)
    this.activate(link)
  }

  update (entries) {
    entries
      .filter((entry) => entry.isIntersecting)
      .forEach((entry) => {
        const link = this.linkTargets.find((target) => this.sectionFor(target) === entry.target)
        if (link) this.activate(link)
      })
  }

  activate (current) {
    this.linkTargets.forEach((link) => {
      const active = link === current
      link.classList.toggle(this.activeClass, active)
      if (active) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  sectionFor (link) {
    return link.hash ? document.getElementById(link.hash.slice(1)) : null
  }

  get sections () {
    return this.linkTargets.map((link) => this.sectionFor(link)).filter(Boolean)
  }
}
